/**
 * Constantes globais da aplicação.
 *
 * Centraliza valores mágicos referenciados pela spec para evitar duplicação
 * e facilitar ajustes futuros (limites de paginação, retries de SSE etc.).
 *
 * Referências da spec:
 *   • §9 Paginação — `page_size` default 20, máx 100 (alinhado com Orchestrator).
 *   • §9 SSE useSSE — "Reconnect com backoff exponencial (máx 3 tentativas)".
 *   • §15 Notificações — rehidratação inicial via `GET /executions?page=1&page_size=20`.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Paginação
// ─────────────────────────────────────────────────────────────────────────────

/** Tamanho padrão de página para listagens (DataTable, queries paginadas). */
export const PAGE_SIZE = 20

/** Limite máximo aceito pelo Orchestrator em qualquer endpoint paginado. */
export const MAX_PAGE_SIZE = 100

/** Opções de page size disponíveis no seletor da DataTable. */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

// ─────────────────────────────────────────────────────────────────────────────
// SSE (Server-Sent Events)
// ─────────────────────────────────────────────────────────────────────────────

/** Tentativas máximas de reconexão SSE antes de desistir. */
export const SSE_MAX_RETRIES = 3

/** Delay base (ms) para backoff exponencial: delay = BASE * 2^attempt. */
export const SSE_BACKOFF_BASE_MS = 1000

/** Delay máximo (ms) entre tentativas de reconexão SSE. */
export const SSE_BACKOFF_MAX_MS = 30_000

// ─────────────────────────────────────────────────────────────────────────────
// WebSocket (Live Preview)
// ─────────────────────────────────────────────────────────────────────────────

/** Tentativas máximas de reconexão WebSocket antes de notificar erro fatal. */
export const WS_MAX_RETRIES = 3

/** Delay base (ms) para reconexão WebSocket. */
export const WS_BACKOFF_BASE_MS = 1000

// ─────────────────────────────────────────────────────────────────────────────
// HTTP / API
// ─────────────────────────────────────────────────────────────────────────────

/** Timeout default para requisições HTTP comuns (ms). */
export const API_TIMEOUT_MS = 30_000

/** Timeout estendido para uploads multipart (ex.: voice samples). */
export const API_UPLOAD_TIMEOUT_MS = 120_000

/**
 * Timeout para chamadas síncronas que esperam o LLM (curadoria). A correção
 * de estrutura roda até duas passadas no modelo local, cada uma pontuada no
 * Worker — medido em dev: 21 s a 104 s. Com o default de 30 s o browser
 * desistia enquanto o servidor ainda respondia 200.
 */
export const API_LLM_TIMEOUT_MS = 180_000

/** Header de tenant exigido pela API multi-tenant. */
export const TENANT_HEADER = 'X-Tenant-ID'

// ─────────────────────────────────────────────────────────────────────────────
// Storage (localStorage keys)
// ─────────────────────────────────────────────────────────────────────────────

/** Prefixo único para isolar todas as chaves do app no localStorage. */
export const STORAGE_PREFIX = 'itap-ura'

export const STORAGE_KEYS = {
  AUTH: `${STORAGE_PREFIX}:auth`,
  TENANT: `${STORAGE_PREFIX}:tenant`,
  THEME: `${STORAGE_PREFIX}:theme`,
  LANGUAGE: `${STORAGE_PREFIX}:language`,
  SIDEBAR: `${STORAGE_PREFIX}:sidebar`,
  PREVIEW: `${STORAGE_PREFIX}:preview`,
} as const

// ─────────────────────────────────────────────────────────────────────────────
// UI
// ─────────────────────────────────────────────────────────────────────────────

/** Duração default de toast (ms) — alinhado com Sonner default (§15). */
export const TOAST_DURATION_MS = 4000

/** Largura fixa do PropertiesPanel no editor visual (px, spec §12). */
export const PROPERTIES_PANEL_WIDTH = 320

/** Debounce default (ms) para inputs de busca/filtro em DataTables. */
export const SEARCH_DEBOUNCE_MS = 300

// ─────────────────────────────────────────────────────────────────────────────
// Datas / Formatação
// ─────────────────────────────────────────────────────────────────────────────

/** Formato canônico de data+hora exibido na UI (spec — dd/MM/yyyy HH:mm). */
export const DATE_TIME_FORMAT = 'dd/MM/yyyy HH:mm'

/** Formato curto somente data. */
export const DATE_FORMAT = 'dd/MM/yyyy'

/** Formato somente hora (usado em logs de eventos SSE, §14). */
export const TIME_FORMAT = 'HH:mm:ss'
